import { api, cleanParams } from "@/common/http/axios";

// ============== 用户 ==============

export interface AdminUserVo {
  id: number;
  email: string;
  name: string;
  username?: string | null;
  avatarUrl?: string | null;
  source: string;
  status: number;
  hasLocal: boolean;
  hasFeishu: boolean;
  feishuEmail?: string | null;
  boundAtLocal?: string | null;
  boundAtFeishu?: string | null;
  lastLoginAt?: string | null;
  createTime?: string | null;
  roles: string[];
}

export interface PageMeta {
  current: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ListUsersParams {
  keyword?: string;
  source?: string;
  role?: string;
  status?: number;
  current?: number;
  pageSize?: number;
}

export const listUsers = async (
  params: ListUsersParams,
): Promise<{ items: AdminUserVo[]; page: PageMeta }> => {
  const res = await api.get("/admin/users", { params: cleanParams(params) });
  return {
    items: (res.data?.data as AdminUserVo[]) ?? [],
    page: res.data?.page as PageMeta,
  };
};

export const getUser = async (id: number): Promise<AdminUserVo | null> => {
  const res = await api.get(`/admin/users/${id}`);
  return (res.data?.data as AdminUserVo) ?? null;
};

export interface CreateUserPayload {
  email: string;
  name: string;
  username: string;
  password: string;
  roleCodes?: string[];
}

export interface CreateUserConflict {
  exists: true;
  user: AdminUserVo;
}

export type CreateUserResult =
  | { ok: true; user: AdminUserVo }
  | { ok: false; code: number; message: string; existing?: AdminUserVo };

export const createUser = async (payload: CreateUserPayload): Promise<CreateUserResult> => {
  const res = await api.post("/admin/users", payload);
  const body = res.data;
  if (body?.code === 0) {
    return { ok: true, user: body.data as AdminUserVo };
  }
  const conflict = body?.data as CreateUserConflict | undefined;
  return {
    ok: false,
    code: body?.code,
    message: body?.message ?? "创建失败",
    existing: conflict?.user,
  };
};

export interface UpdateUserPayload {
  name?: string;
  status?: number;
  avatarUrl?: string;
}

export const updateUser = async (id: number, payload: UpdateUserPayload): Promise<AdminUserVo> => {
  const res = await api.put(`/admin/users/${id}`, payload);
  return res.data?.data as AdminUserVo;
};

export const deleteUser = async (id: number): Promise<void> => {
  await api.delete(`/admin/users/${id}`);
};

export const assignUserRoles = async (
  id: number,
  roleCodes: string[],
): Promise<AdminUserVo> => {
  const res = await api.post(`/admin/users/${id}/roles`, { roleCodes });
  return res.data?.data as AdminUserVo;
};

export const resetUserPassword = async (id: number, newPassword: string): Promise<void> => {
  await api.post(`/admin/users/${id}/reset-password`, { newPassword });
};

export const bindUserLocal = async (
  id: number,
  username: string,
  password: string,
): Promise<AdminUserVo> => {
  const res = await api.post(`/admin/users/${id}/bind-local`, { username, password });
  return res.data?.data as AdminUserVo;
};

export const unbindUserFeishu = async (id: number): Promise<AdminUserVo> => {
  const res = await api.post(`/admin/users/${id}/unbind-feishu`);
  return res.data?.data as AdminUserVo;
};


// ============== 角色 ==============

export interface AdminRoleVo {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  builtin: boolean;
  permissions: string[];
}

export const listRoles = async (): Promise<AdminRoleVo[]> => {
  const res = await api.get("/admin/roles");
  return (res.data?.data as AdminRoleVo[]) ?? [];
};

export const createRole = async (payload: {
  code: string;
  name: string;
  description?: string;
}): Promise<AdminRoleVo> => {
  const res = await api.post("/admin/roles", payload);
  return res.data?.data as AdminRoleVo;
};

export const updateRole = async (
  id: number,
  payload: { name?: string; description?: string },
): Promise<AdminRoleVo> => {
  const res = await api.put(`/admin/roles/${id}`, payload);
  return res.data?.data as AdminRoleVo;
};

export const deleteRole = async (id: number): Promise<void> => {
  await api.delete(`/admin/roles/${id}`);
};

export const assignRolePermissions = async (
  id: number,
  permissionCodes: string[],
): Promise<AdminRoleVo> => {
  const res = await api.post(`/admin/roles/${id}/permissions`, { permissionCodes });
  return res.data?.data as AdminRoleVo;
};


// ============== 权限点 ==============

export interface AdminPermissionVo {
  id: number;
  code: string;
  name: string;
  type: string;
  parentCode?: string | null;
  description?: string | null;
}

export const listPermissions = async (): Promise<AdminPermissionVo[]> => {
  const res = await api.get("/admin/permissions");
  return (res.data?.data as AdminPermissionVo[]) ?? [];
};
